import { SystemSettings } from '@ai/common/src/types/config';
import { TRAIN_STATUS, DIALOG_EXPORT_KEY, LabelTranslations } from '@ai/common/src/types/expert';
import { YmGoal } from '@ai/common/src/utils/yandexMetrikaUtils';
import { DialogChartMetric, MessageChartMetric, Period, Unit } from '@constants/chartConstants';
import { VoiceGroup } from '@sections/expert-configuration/SpeechTab/SpeechTab';
import { EXPERT_TAB } from '@type/expert';

export const ru = {
  title: 'AI Эксперт',
  version: 'Версия',
  clickToCopy: 'Нажмите, чтобы скопировать',
  copied: 'Скопировано',
  avatar: 'Аватар',
  random: 'Случайный',
  link: 'Ссылка',
  imageUrl: 'Ссылка на изображение',
  file: 'Файл',
  animated: 'Анимированный',
  add: 'Добавить',
  all: 'Все',
  unknown: 'Неизвестно',
  noData: 'Нет данных',
  example: 'Пример',
  or: 'или',
  notProvided: 'не указан',

  goodMorning: 'Доброе утро',
  goodAfternoon: 'Добрый день',
  goodEvening: 'Добрый вечер',
  goodNight: 'Доброй ночи',

  save: 'Сохранить',
  cancel: 'Отмена',
  download: 'Скачать',
  description: 'Описание',
  expert: 'Эксперт',
  experts: 'Эксперты',
  student: 'Обучающийся',
  students: 'Обучающиеся',
  comment: 'Комментарий',
  enter: 'Ввод',
  error: 'Ошибка',
  errorOccured: 'Произошла ошибка',
  goHome: 'На главную',
  loading: 'Загрузка...',
  no: 'Нет',
  undefined: 'Не определено',
  yes: 'Да',
  attention: 'Внимание',
  language: 'Язык',
  confirm: 'Подтвердить',
  searchText: 'Поиск по тексту',
  debug: 'Отладка',
  delete: 'Удалить',
  tokens: 'Токены',
  cost: 'Стоимость',
  date: 'Дата',
  content: 'Содержание',
  durationSec: 'Длительность, сек',
  speechRecognitionSec: 'Распознавание, сек',
  speechSynthesisSec: 'Синтез, сек',
  speechSynthesisCount: 'Синтез, символов',
  type: 'Тип',
  placeholderExpertName: 'Новый эксперт',
  placeholderStudentName: 'Новый обучающийся',
  created: 'Создан',
  pin: 'Закрепить',
  unpin: 'Открепить',
  continue: 'Продолжить',
  serverIsNotResponding: 'Сервер не отвечает. Повторите запрос позже',
  serverRespond502: 'Запрошенная функция временно недоступна',
  of: 'из',
  noOptions: 'Нет доступных вариантов',

  validation: {
    requiredField: 'Обязательное поле',
    bothRequiredOrNone: 'Оба поля должны быть заполнены или оба пусты',
    maxLength: 'Максимальная длина - {{length}} символов',
    minLength: 'Минимальная длина - {{length}} символов',
    fieldError: 'Некоторые поля заполнены некорректно: {{fields}}.',
    notSupportedFormat: 'Неподдерживаемый формат',
    fileIsTooLarge: 'Максимальный размер — {{size}}',
    fileUploadError: 'Ошибка загрузки файла',
    invalidMcpServers: 'Ошибка в создании MCP-сервера',
    mcpServerNameRequired: 'Имя сервера не может быть пустым',
    invalidUrl: 'Ошибка в URL',
    keyRequiredForValue: 'Не задан ключ',
  },

  expertFormLabels: {
    name: 'Имя эксперта',
    image_id: 'Аватар',
    'config.system_prompt': 'Системный промпт',
    'config.system_prompt_voice_chat': 'Промпт для голосового чата',
    'config.system_prompt_text_chat': 'Промпт для текстового чата',

    'config.first_sentense': 'Первое сообщение',
    'config.use_url_context': 'Читать содержимое ссылок',
    'config.temperature': '',
    'config.llm': '',
    'config.max_tokens': 'Максимальное количество токенов',
    'config.rag_chunk_size': 'Размер фрагментов контекста',
    'config.rag_chunk_count': 'Количество фрагментов в контексте',
    'config.use_extended_doc_processing': 'Расширенная обработка документов',
    'config.use_image_vision': 'Vision-описание изображений',
    'config.show_answer_source': 'Показывать источники ответа',
    'config.emotion': '',
    'config.voice': '',
    'config.speed': 'Скорость речи',
    'config.stt_pause_between_words': 'Паузы между словами',
    'config.before_start_text': 'До начала разговора',
    'config.listening_status_text': 'Эксперт слушает',
    'config.speaking_status_text': 'Эксперт говорит',
    'config.no_interrupt_speaking_status_text':
      'Эксперт говорит в режиме, когда его нельзя перебить',
    'config.thinking_status_text': 'Эксперт думает',
    'config.case_sensitive_replacements': 'Аббревиатуры и термины',
    'config.case_insensitive_replacements': 'Длинные слова',
    'config.timezone': 'Часовой пояс',
    'config.yandex_calendar.caldav_url': 'CalDAV URI календаря',
    'config.yandex_calendar.caldav_password': 'Пароль для календаря',
    'config.widget_size': 'Компактный вариант',
    'config.tg_tokens': 'Токены Telegram-ботов для связи с операторами',
    'config.safe_interrupt': 'Безопасное перебивание',
    'config.google_sheets.spreadsheet_url': 'Ссылка на таблицу',
    'config.google_sheets.columns_mapping': 'Отображаемые колонки',
    'config.is_published': 'Опубликован',
    'config.dialog_analisys_additional_fields': 'Опишите дополнительные поля',
    'config.suggested_questions': 'Стартовые подсказки',
    'config.use_suggested_questions': 'Автоподсказки',
    'config.allow_profanity': 'Ненормативная лексика разрешена',
    'config.allow_emoji': 'Смайлики разрешены',
    'config.allow_offtop': 'Отвлечённые темы разрешены',
    'config.proactive_triggers': 'Активные приглашения',
    'config.proactive_triggers.timer.message': 'Текст сообщения (по таймеру)',
    'config.proactive_triggers.exit_intent.message': 'Текст сообщения (попытка уйти)',
    'config.proactive_triggers.scroll_depth.message': 'Текст сообщения (глубина прокрутки)',
    'config.proactive_triggers.inactivity.message': 'Текст сообщения (неактивность)',
    'config.proactive_triggers.url_match.message': 'Текст сообщения (определённая страница)',
    'config.proactive_triggers.url_match.url': 'Адрес страницы (активное приглашение)',
    'config.allow_calling_telegram_operator': 'Разрешить вызов телеграм-оператора в чат',
    'config.allow_calling_supervisor': 'Разрешить вызов оператора в чат',
  } satisfies LabelTranslations,

  timeFormat: {
    hour: 'ч',
    min: 'мин',
    min_short: 'м',
    sec: 'сек',
    sec_short: 'с',
  },

  chat: {
    clearChat: 'Очистить чат',
    enterMessage: 'Введите сообщение',
    chatLanguage: 'Язык чата',
    maxTokensError: 'Ответ превысил лимиты и был прерван',
    prohibitedContentRequestError: 'Запрос содержит недопустимые темы',
    prohibitedContentResponseError: 'Ответ содержит недопустимые темы',
    simulateVoiceMode: 'Имитация голосового режима',
    simulateVoiceModeDescr: 'Имитирует общение голосом: ответ формируется с приоритетом скорости.',
  },

  player: {
    pause: 'Пауза',
    play: 'Воспроизвести',
    error: 'Ошибка воспроизведения',
    speechLength: 'Длительность речи',
    signalNoise: 'Шум',
    reverberation: 'Реверберация',
    quality: 'Качество',
    recordMarkupAlgorithm: 'Алгоритм маркировки',
    lowFrequencyBoundary: 'Нижняя частота',
    highFrequencyBoundary: 'Верхняя частота',
    reverbMomentsCount: 'Количество моментов реверберации',
    maxNotClippedSegmentLength: 'Максимальная длительность сегмента',
    errorPlayer: 'Ошибка плеера',
  },

  login: {
    welcomeText:
      'Веб-сервис, который позволяет создавать виртуальных AI-экспертов на основе передовых технологий LLM (Large Language Models) и RAG (Retrieval-Augmented Generation).',
    welcomeSubtext:
      'AI Эксперт использует передовые технологии языковых моделей для предоставления глубоких аналитических данных и понимания текстовой информации. Разработанный для различных применений, этот инструмент предлагает непревзойденную точность в интерпретации текста, анализе настроений и понимании контекста. Независимо от того, структурируете ли вы данные, извлекаете ключевые идеи или исследуете сложные повествования, AI Эксперт создан для удовлетворения ваших потребностей в лингвистическом анализе с точностью и эффективностью.',

    registration: 'Регистрация',
    accountQuestion: 'У вас уже есть аккаунт?',
    enter: 'Войти',
    or: 'или',
    title: 'Вход',
    registerQuestion: 'Нет аккаунта?',
    register: 'Зарегистрироваться',
    signin: 'Войти',

    username: 'Имя пользователя',
    email: 'Электронная почта',
    password: 'Пароль',
    passwordConfirm: 'Повторите пароль',

    agreeProcessingPersonalData: 'Я согласен на обработку персональных данных',

    error: {
      wrongEmail: 'Пожалуйста, введите корректный адрес электронной почты',
      passwordMatch: 'Пароли должны совпадать',
      wrongEmailOrPassword: 'Неверный логин или пароль',
      noToken: 'Не указан токен пользователя',
      noEmail: 'Не указан адрес электронной почты пользователя',
      maxLength: 'Максимальная длина - {{length}} символов',
      userDisabled: 'Пользователь заблокирован',
    },
    required: {
      username: 'Введите имя пользователя',
      email: 'Введите адрес электронной почты',
      password: 'Введите пароль',
    },
    logout: 'Выйти',
    errorRegister:
      'Во время регистрации произошла ошибка. Пожалуйста, проверьте введённые данные и попробуйте снова. Если вы уже зарегистрированы, вы можете сбросить пароль.',

    sendLink: 'Отправить ссылку',
    resendLink: 'Отправить ссылку повторно',
    linkError:
      'Ссылка недействительна или срок её действия истёк. Пожалуйста, запросите новую ссылку.',

    verificationLinkSent: 'Письмо со ссылкой для подтверждения отправлено на email {{email}}.',
    verificationSuccess: 'Email успешно подтвержден! Теперь вы можете войти в систему.',

    forgotPassword: 'Забыли пароль?',
    goToAuth: 'Перейти к авторизации',
    enterEmailForReset:
      'Укажите email, который вы использовали при регистрации, и мы отправим вам ссылку для сброса пароля.',
    resetLinkSent: 'Письмо со ссылкой для сброса пароля отправлено на email {{email}}.',
    createNewPassword: 'Создайте новый пароль для вашего аккаунта.',
    passwordResetSuccess: 'Пароль успешно изменён!',
  },

  expertsList: {
    deleteExpert: 'Удалить эксперта',
    deleteExpertConfirm:
      'Вы действительно хотите удалить эксперта {{name}}?\n\nУдаление эксперта повлечёт за собой удаление всех связанных данных, включая диалоги и статистику.\n\nЭто действие нельзя отменить.',

    duplicateExpert: 'Cоздать копию эксперта',
    duplicateTo: ' у пользователя ',
    duplicateExpertConfirm: 'Вы действительно хотите создать копию эксперта',
    duplicateSuccess: 'Эксперт «{{name}}» успешно скопирован пользователю с ID {{userId}}',
    duplicateError: 'Произошла ошибка при копировании эксперта',

    expertNotSelected: 'Эксперт не выбран',
    selectExpert:
      'Выберите эксперта из списка или создайте нового, чтобы продолжить конфигурирование голосового ассистента',
    createExpert: 'Создать эксперта',
    openMenu: 'Открыть меню',
    advancedMode: 'Расширенный режим',
    publishedDescr: 'Если эксперт опубликован, он будет добавляться всем новым пользователям.',
    expertManagerInfo:
      'Все ваши опубликованные эксперты будут автоматически добавляться в аккаунты новых пользователей как предустановленные.',
    published: 'Опубликован',
    notPublished: 'В работе',
    useTools: 'Включить использование инструментов',
    useToolsDescr:
      'При включении, эксперту станут доступны инструменты для поиска в базе знаний, а также возможности по подключению серверов MCP для расширения списка инструментов',

    expertStatus: {
      [TRAIN_STATUS.completed]: 'Обучен',
      [TRAIN_STATUS.failed]: 'Ошибка',
      [TRAIN_STATUS.in_progress]: 'Обучается',
      [TRAIN_STATUS.pending]: 'Не обучен',
      unknown: 'Не определен',
    },

    expertTabs: {
      [EXPERT_TAB.EXPERT]: 'Эксперт',
      [EXPERT_TAB.TRAINING]: 'Обучение',
      [EXPERT_TAB.CHAT]: 'Чат',
      [EXPERT_TAB.WIDGET]: 'Виджет',
      [EXPERT_TAB.HISTORY]: 'История',
      [EXPERT_TAB.SPEECH]: 'Речь',
      [EXPERT_TAB.INTEGRATIONS]: 'Интеграции',
      [EXPERT_TAB.TOOLS]: 'Инструменты',
    },

    systemPromptDescr:
      'Системный промпт нужен для определения характера эксперта и контекста разговора.',
    useUrlContextDescr:
      'Включите, чтобы эксперт читал и анализировал содержимое веб-страниц из ссылок в сообщении.',
    allowProfanityDescr:
      'Если включено, эксперт может использовать ненормативную лексику, если это уместно в контексте. Если выключено — мат запрещён при любых обстоятельствах.',
    allowEmojiDescr:
      'Если включено, эксперт может использовать смайлики, если это уместно или пользователь явно просит. Если выключено — смайлики запрещены при любых обстоятельствах.',
    allowOfftopDescr:
      'Если включено, эксперт может общаться на любые темы. Если выключено — строго придерживается своей роли и вежливо отказывается от посторонних вопросов.',

    firstMessageDescr:
      'Первая фраза, которую скажет эксперт. Если её нет, эксперт будет ждать, пока пользователь начнёт разговор.',
    suggestedQuestionsDescr:
      'Вопросы или фразы, которые будут предложены пользователю при первом обращении к эксперту.',
    useSuggestedQuestionsDescr:
      'Автоматически предлагать пользователю варианты продолжения диалога после каждого ответа эксперта.',
    addQuestionPlaceholder: 'Введите вопрос...',
    generateQuestions: 'Сгенерировать',
    generateQuestionsDisabledHint:
      'Для генерации стартовых подсказок необходимо заполнить системный промпт эксперта.',
    llmAndTemperature: 'LLM и температура',
    llm: 'LLM',
    speed: 'Скорость',
    intelligence: 'Интеллект',
    contextWindow: 'Размер контекста',
    llmAndTemperatureDescr:
      'Выберите используемую языковую модель и температуру.\nЧем ниже значение температуры,\
       тем более детерминированными будут результаты. Увеличение температуры может привести к большей случайности,\
        что способствует более разнообразным или творческим результатам.',
    llmDescr: 'Выберите используемую языковую модель.',
    maxTokensDescr:
      'Эта настройка определяет, какой максимальный объем текста может сгенерировать эксперт в одном ответе.\
     Чем больше значение, тем длиннее и детальнее может быть ответ, но при этом увеличивается время генерации.\
     \n\nЛимит в 500 токенов подходит для описания услуг или развёрнутых ответов на вопросы.\
     Значения около 10000 токенов подходят для создания очень подробных текстов, например,\
       длинных статей или многоступенчатых инструкций. \
     \n\nОдно слово на русском языке в среднем занимает 1.5–2 токена.',
    recognitionSettings: 'Настройки распознавания',
    recognitionSettingsDescr:
      'Эти параметры определяют, как система отличает речь от тишины или фонового шума. Точная настройка помогает улучшить качество и скорость распознавания.',
    pausesBetweenWordsDescr:
      'Определяет, как долго должна продолжаться тишина (в миллисекундах), чтобы система посчитала текущую фразу завершенной и начала обработку. Увеличение этого значения поможет объединить фразы с короткими паузами в одну.',

    voiceAndCharacter: 'Голос и характер',
    voiceDescr: 'Выберите голос и характер произношения эксперта.',

    voiceGroups: {
      [VoiceGroup.YANDEX]: 'Голоса Яндекс',
      [VoiceGroup.GOOGLE]: 'Голоса Google',
      [VoiceGroup.ELEVENLABS]: 'Голоса ElevenLabs',
      otherVoices: 'Другие голоса',
    },

    embedCode: 'Код для вставки',
    embedCodeDescr:
      'Добавьте следующий фрагмент на страницы, на которых вы хотите разместить виджет.',
    avatarDescr: 'Выберите один из случайных аватаров или добавьте своё собственное изображение.',
    randomDescr: 'Нажмите на аватар, чтобы изменить изображение.',
    texts: 'Тексты',
    textsDescr: 'Измените тексты, отображаемые в виджете.',

    speedDescr: 'Скорость, с которой говорит эксперт.',
    endPhrasesDescr:
      'Если одна из этих фраз присутствует в реплике эксперта, эта реплика будет считаться последней\
     в текущем диалоге, и диалог завершится после того, как эксперт закончит речь, если только пользователь не прервет ее.',
    contextSettings: 'Настройки контекста и поиска',
    contextSettingsDescr:
      'Настройте, как система обрабатывает и использует информацию для формирования ответов.',
    ragChunkSizeDescr:
      'Эта настройка определяет размер фрагментов данных (в символах), используемых для формирования контекста.\
      \n\nСлишком маленький размер может привести к потере смысловой целостности.\
      \nСлишком большой — к включению избыточной информации, снижению релевантности и замедлению ответа.\
      \nОптимальный размер зависит от структуры данных и типа запросов.\
      \n\nИзменение этой настройки потребует полного переобучения эксперта.',
    ragChunkCountDescr:
      'Эта настройка определяет, сколько фрагментов данных будет добавлено в контекст для генерации ответа.\
       Чем больше фрагментов используется, тем точнее и детальнее будет ответ, но при этом возрастает время обработки.',
    extendedDocProcessingDesc:
      'Использовать расширенные инструменты обработки документов с возможностью извлечения изображений и функциями OCR.',
    imageVisionDesc:
      'Анализировать изображения в документах и загружаемые файлы-изображения через vision-модель. Текстовое описание изображений будет сохранено в базу знаний и доступно для семантического поиска.\n\nИзменение этой настройки требует полного переобучения эксперта.',
    imageVisionModelDesc: 'Выберите vision-модель для анализа изображений.',
    imageVisionDisabledTooltip:
      'Для обучения на этом файле включите режим Vision в расширенных настройках',
    documentStatus: {
      pending: 'Ожидает обработки',
      in_progress: 'Обрабатывается',
      completed: 'Успешно',
      failed: 'Ошибка',
    },
    useElasticSearchDescr:
      'Включение этой опции улучшает распознавание сокращений, названий и специализированных терминов в тексте.\nОднако это может увеличить время обработки запроса.',
    showAnswerSourceDescr:
      'Показывать в каждом ответе эксперта названия и фрагменты исходных документов.\nЭто повышает прозрачность и позволяет проверить контекст ответа. ',
    soundTest: 'Аудио-превью',
    soundTestDescr: 'Прослушайте, как будет звучать голос с текущими настройками.',
    listen: 'Послушать',
    textForSynthesis: 'Текст для синтеза',
    widgetSizeDescr: 'Включите эту опцию, чтобы уменьшить виджет.',
    proactiveTriggers: {
      sectionTitle: 'Активные приглашения',
      sectionDescr:
        'Виджет, добавленный на ваш сайт, будет сам открывать чат и отправлять персонализированное сообщение от эксперта в нужный момент, чтобы помочь увеличить конверсию и вовлечённость.\nСрабатывает первое выполненное условие, остальные автоматически отменяются.',
      messageLabel: 'Текст сообщения',
      testButton: 'Тест',
      cooldownLabelStart: 'Показывать не чаще одного раза в',
      cooldownLabelEnd: 'ч.',
      timer: {
        title: 'По таймеру',
        descrStart: 'Чат открывается через',
        descrEnd: 'секунд после загрузки страницы.',
        delayLabel: 'Задержка (секунд)',
        messagePlaceholder: 'Привет! Могу ответить на ваши вопросы 👋',
      },
      exit_intent: {
        title: 'Попытка уйти со страницы',
        descr: 'Курсор выходит за верхний край браузера или резкий скролл вверх на телефоне.',
        messagePlaceholder: 'Подождите! Возможно, я смогу помочь вам найти то, что вы ищете',
      },
      scroll_depth: {
        title: 'По глубине прокрутки',
        descrStart: 'Пользователь прокрутил страницу на',
        descrEnd: '% и более.',
        messagePlaceholder: 'Вижу, что вы изучаете страницу — есть вопросы? Я готов помочь!',
      },
      inactivity: {
        title: 'Неактивен',
        descrStart: 'Нет никаких действий на странице в течение',
        descrEnd: 'секунд.',
        delayLabel: 'Время неактивности (секунд)',
        messagePlaceholder: 'Похоже, вы изучаете варианты — могу помочь с выбором?',
      },
      url_match: {
        title: 'На определённой странице',
        descr: 'Адрес страницы содержит указанный текст.',
        urlLabel: 'Адрес страницы',
        messagePlaceholder: 'Интересует эта тема? Расскажу подробнее и отвечу на любые вопросы',
      },
    },
    safeInterruptDescr:
      'Отключить перебивание на устройствах с риском эха или самосрабатывания (устройства Apple и мобильный Firefox).',

    pronunciation: {
      title: 'Произношение и ударение',
      descr:
        'Вы можете указать корректное произношение названий и аббревиатур.\
       \n\n• Отмечайте ударения \nДобавляйте перед ударными гласными знак «+», чтобы указать ударение.\
      \nНапример: референс → р+еференс\
      \n\n• Меняйте написание слов \nПопробуйте написать слова так, как они слышатся.\
      \nНапример: ненастный → нен+асный, многопрофильный → мн+ого пр+офильный',
      caseSensitiveDescr:
        'Замена будет происходить только при точном совпадении регистра (это предотвращает ложные\
        замены в составе других слов).\
        \nНапример: IT → айт+и',
      caseInsensitiveDescr:
        'Замена будет происходить независимо от регистра (для обработки различных форм одного слова).\
         \nНапример: видеовстреч → видеовстр+еч',
      word: 'Слово',
      pronunciation: 'Произношение',
    },

    yandexCalendar: {
      title: 'Яндекс.Календарь',
      descr: 'Настройте создание событий в Яндекс.Календаре.',
      descrVerbose:
        'Если в процессе разговора с пользователем эксперт назначает встречу или созвон и получает контакты пользователя (телефон или почту), то в указанном календаре будет создано событие\
     с названием вида "имя_эксперта. имя_пользователя. дата_создания",\
     содержащее полученные контактные данные, описание задачи и другие данные из разговора.\
     \n\n Для использования этой функции необходимо явно включить в системный промпт эксперта инструкцию обрабатывать\
      запросы на назначение встреч и созвонов, запрашивать контакты. \
     \n\nЕсли пользователь запланирует несколько встреч одновременно,\
      система может интерпретировать это как единый запрос и создать только одно событие.',
      uriForm: 'URI вида ',
      caldavUrlInstruction:
        "1. В списке календарей нажмите на иконку ⚙ у нужного календаря\
                    \n2. Перейдите во вкладку 'Экспорт'\
                    \n3. Скопируйте содержимое поля CalDAV",

      passwordDescr: 'Специальный пароль, который даёт доступ только к календарю',
      followLink: 'Перейдите по ссылке ',
      createPassword: " и создайте новый пароль для приложения 'Календарь'",
      timezoneDescr: 'Часовой пояс, в котором будут создаваться события календаря',
    },

    googleSheets: {
      title: 'Google Таблицы',
      descr: 'Настройте экспорт итогов диалога в вашу Google-таблицу.',
      columnName: 'Название колонки',
      columnsMappingDescr:
        'Выберите, какие данные вы хотите записывать в вашу Google-таблицу.\
        Для каждого поля укажите точное название колонки из первой строки вашей таблицы — именно туда будут попадать значения.\
\nЕсли название не указано или такой колонки нет в таблице, данные из этого поля записаны не будут.\
 Вы также можете исключить поле из выгрузки, нажав кнопку «Скрыть колонку».',

      dataKey: {
        [DIALOG_EXPORT_KEY.customer_email]: {
          title: 'Email пользователя',
          descr: 'Email пользователя, полученный из диалога',
        },
        [DIALOG_EXPORT_KEY.customer_name]: {
          title: 'Имя пользователя',
          descr: 'Имя пользователя, полученное из диалога',
        },
        [DIALOG_EXPORT_KEY.customer_phone]: {
          title: 'Номер телефона пользователя',
          descr: 'Номер телефна пользователя, полученный из диалога',
        },
        [DIALOG_EXPORT_KEY.customer_tz]: {
          title: 'Часовой пояс пользователя',
          descr: 'Часовой пояс, из которого клиент вёл диалог',
        },
        [DIALOG_EXPORT_KEY.dialog_id]: { title: 'ID диалога', descr: '' },
        [DIALOG_EXPORT_KEY.event_datetime]: {
          title: 'Дата события',
          descr: 'Дата встречи или созвона, который был назначен в ходе диалога',
        },
        [DIALOG_EXPORT_KEY.expert_id]: { title: 'ID эксперта', descr: '' },
        [DIALOG_EXPORT_KEY.task_short]: {
          title: 'Задача пользователя (кратко)',
          descr: 'Краткое описание задачи пользователя, полученное из диалога',
        },
        [DIALOG_EXPORT_KEY.task_verbose]: {
          title: 'Задача пользователя (подробно)',
          descr: 'Подробное описание задачи пользователя, полученное из диалога',
        },
        [DIALOG_EXPORT_KEY.transcription]: {
          title: 'Транскрипция диалога',
          descr: 'Диалог полностью',
        },
        [DIALOG_EXPORT_KEY.created_at]: { title: 'Дата', descr: 'Дата создания записи' },
        [DIALOG_EXPORT_KEY.field_1]: { title: 'Поле 1', descr: 'Дополнительное поле' },
        [DIALOG_EXPORT_KEY.field_2]: { title: 'Поле 2', descr: 'Дополнительное поле' },
        [DIALOG_EXPORT_KEY.field_3]: { title: 'Поле 3', descr: 'Дополнительное поле' },
        [DIALOG_EXPORT_KEY.field_4]: { title: 'Поле 4', descr: 'Дополнительное поле' },
        [DIALOG_EXPORT_KEY.field_5]: { title: 'Поле 5', descr: 'Дополнительное поле' },
      },
      showColumn: 'Показать колонку',
      hideColumn: 'Скрыть колонку',
      spreadsheetUrlDescr:
        'Для работы интеграции необходимо в настройках доступа таблицы добавить сервисного пользователя AI-платформы с ролью редактора.',
    },

    metrika: {
      title: 'Яндекс Метрика',
      descr: 'Настройте отправку целей Яндекс Метрики для отслеживания событий виджета.',
      descrVerbose:
        'Для этого необходимо:\
      \n1. Перейти в нужный счётчик → Цели → Добавить цель\
      \n2. В окне добавления цели в качестве типа условия нужно выбрать "JavaScript событие",\
       а в идентификаторе цели выбрать "совпадает" и указать цель, которую нужно отслеживать.\
      \n\nДоступные идентификаторы событий для Яндекс Метрики:',

      [YmGoal.nlab_text_dialog_started]: {
        title: 'Пользователь начал текстовый диалог',
        descr: 'Пользователь нажал кнопку текстового чата.\nПоказывает интерес к взаимодействию.',
      },
      [YmGoal.nlab_voice_dialog_started]: {
        title: 'Пользователь начал голосовой диалог',
        descr:
          'Пользователь нажал кнопку микрофона для старта голосового диалога.\nОтражает готовность общаться голосом.',
      },
      [YmGoal.nlab_user_first_message_sent]: {
        title: 'Пользователь отправил первое сообщение',
        descr: 'Пользователь ввёл или произнёс первое сообщение.\nФакт начала реального диалога.',
      },
      [YmGoal.nlab_switched_to_text_mode]: {
        title: 'Пользователь переключился на текстовый режим',
        descr:
          'Пользователь отправил текстовое сообщение в голосовом чате.\nВысокий процент может указывать на то, что голосовые ответы слишком сложные для восприятия на слух или содержат детали, которые пользователь хочет видеть письменно (цифры, ссылки, инструкции).',
      },
      [YmGoal.nlab_switched_to_voice_mode]: {
        title: 'Пользователь переключился на голосовой режим',
        descr:
          'Пользователь отправил голосовое сообщение в текстовом чате.\nВысокий процент может указывать на то, что текстовый ввод не всегда удобен для текущих сценариев использования — особенно на мобильных устройствах или при сложных запросах.',
      },
      [YmGoal.nlab_user_returned_to_dialog]: {
        title: 'Пользователь вернулся к диалогу спустя время',
        descr:
          'Сообщение отправлено после завершения предыдущей сессии.\nГоворит о сохранении интереса.',
      },
      [YmGoal.nlab_operator_requested]: {
        title: 'Пользователь позвал оператора',
        descr:
          'ИИ-эксперт определил, что пользователь хочет общаться с оператором.\nПозволяет отслеживать частоту таких запросов и строить воронки взаимодействия.',
      },
      // [YmGoal.nlab_operator_joined_dialog]: {
      //   title: 'Telegram-оператор подключился к диалогу',
      //   descr:
      //     'Открыто соединение между свободным Telegram-оператором и пользователем.\nФиксирует факт участия человека в диалоге, помогает отслеживать загруженность команды поддержки.',
      // },
      [YmGoal.nlab_supervisor_joined_dialog]: {
        title: 'Оператор поддержки подключился к диалогу',
        descr:
          'Оператор отправил сообщение в диалог.\nЧастота подключений супервайзера помогает оценить сложность запросов и необходимость контроля качества диалогов.',
      },
    },

    documents: 'Документы',
    documentsDescr:
      'Предоставьте LLM информацию по конкретной предметной области, чтобы помочь эксперту точнее отвечать на вопросы.',
    noDocumentsAdded: 'Не добавлено ни одного документа',
    trainExpert: 'Обучить эксперта',
    train: 'Обучить',
    loadingFile: 'Загрузка файла...',
    documentsDndTitle: 'Перетащите документы или нажмите, чтобы выбрать файлы.',
    deleteDocumentConfirm:
      'Вы действительно хотите удалить документ {{title}}? \n\nУдаление документа потребует полного переобучения эксперта. \n\nЭто действие нельзя отменить.',
    notSupportedFormat: 'Неподдерживаемый формат',
    supportedFormats: 'Поддерживаемые форматы',

    imageDndTitle: 'Перетащите изображение или нажмите, чтобы выбрать файл.',
    usageStatistics: 'Статистика использования',
    mcpServers: 'MCP-серверы',
    mcpServersDescr:
      'Подключите внешние MCP-серверы, чтобы расширить возможности эксперта инструментами. \
Эксперт будет использовать их автоматически в процессе диалога.',
    mcpServersEmpty: 'MCP-серверы не добавлены.',
    mcpServerName: 'Название сервера',
    mcpServerNamePlaceholder: 'my_server',
    mcpServerUrl: 'URL',
    mcpServerTransport: 'Транспорт',
    mcpServerAdd: 'Добавить сервер',

    tgOperator: 'Подключение операторов',
    tgOperatorDescr:
      'Пользователь может переключиться из режима общения с AI-экспертом в режим общения с оператором поддержки, \
    попросив об этом эксперта в чате.',
    tgTokensDescr:
      'Создайте один или несколько Telegram-ботов и введите сюда их токены, ввод каждого токена заканчивайте нажатием клавиши Enter. \
      \n\nШаги по созданию бота:\
\n1. Найдите чат @BotFather в Telegram (официальный бот с галочкой).\
\n2. Начните диалог командой Старт (/start).\
\n3. Создайте нового бота командой /newbot.\
\n4. Придумайте название для бота (как его видят пользователи).\
\n5. Придумайте username (логин), который должен заканчиваться на bot (например, MyAwesomeBot).\
\n6. Получите токен – это уникальный ключ доступа, который придет в ответном сообщении. Токен следует хранить в секрете.',

    allowCallingOperator: 'Вызов оператора поддержки в чат',
    allowCallingOperatorDescr:
      'Пользователь может попросить эксперта в чате позвать оператора поддержки, \
   который вступит в диалог вместо эксперта. \
    \nВключите, если хотите предоставлять пользователям возможность вызывать оператора.',
  },

  dashboard: {
    dashboard: 'Обзор',
    allExperts: 'Все эксперты',
    allUsers: 'Все пользователи',
    chooseExpert: 'Выбрать эксперта',

    period: {
      [Period.LAST_DAY]: 'День',
      [Period.LAST_WEEK]: 'Неделя',
      [Period.LAST_MONTH]: 'Месяц',
      [Period.LAST_YEAR]: 'Год',
    },

    metric: {
      [DialogChartMetric.TOTAL_DIALOGS]: 'Количество диалогов',
      [DialogChartMetric.VOICE_DIALOGS]: 'Голосовые диалоги',
      [DialogChartMetric.TEXT_DIALOGS]: 'Текстовые диалоги',
      [DialogChartMetric.VOICE_DIALOGS_AVERAGE_DURATION]: 'Средняя длительность',
      // [DialogChartMetric.TOTAL_COST]: "Общая стоимость",
      // [DialogChartMetric.AVERAGE_COST]: "Средняя стоимость",
      [DialogChartMetric.TOTAL_DURATION]: 'Общая длительность',
      [MessageChartMetric.TOTAL_RESPONSES]: 'Всего ответов',
      [MessageChartMetric.TEXT_RESPONSES]: 'Текстовые ответы',
      [MessageChartMetric.VOICE_RESPONSES]: 'Голосовые ответы',
    },

    mostPopularExpert: 'Самый популярный эксперт',
  },

  chatHistory: {
    chatHistory: 'История диалогов',
    dialogType: 'Тип диалога',
    period: 'Период',
    conversations: 'Разговоры',
    chats: 'Чаты',
    duration: 'Длительность',
    allDialogs: 'Все диалоги',
    noTextDataForChat: 'Недостаточно данных для отображения чата',
    noUserDataForChat: 'Пользователь не задавал вопросы аcсистенту',
    messagesCount: 'Количество сообщений',

    utmParameters: 'UTM-метки',
    utmSource: 'Источник',
    utmMedium: 'Канал',
    utmCampaign: 'Кампания',
    utmContent: 'Контент',
    utmTerm: 'Ключевое слово',
    ip: 'IP',

    activeDialogs: 'Активные диалоги',
    operatorAnswering: 'В чате отвечает Telegram-оператор',
    supervisorAnswering: 'В чате отвечает оператор',
    expertAnswering: 'В чате отвечает ИИ-эксперт',
    releaseDialog: 'Отключиться от чата',

    activeDialogsAlertTitle: 'Диалоги с активностью за последние 30 минут',
    activeDialogsAlertDescr:
      'Вы можете наблюдать переписку в реальном времени или вступить в неё — после отправки сообщения ИИ-эксперт перестанет отвечать, и диалог перейдёт в ваши руки.',
    supervisorOnlineDescription:
      'Вы онлайн. ИИ-эксперты будут перенаправлять на вас диалоги с пользователями, запросившими помощь оператора. Вы будете получать уведомления.',
    supervisorOfflineDescription:
      'Вы офлайн. Включите, чтобы принимать вызовы от пользователей, запросивших помощь оператора.',
  },

  pricing: {
    pricing: 'Тарифы',
    ofVoiceResponces: 'голосовых ответов',
    ofTextResponces: 'текстовых ответов',
    cost: 'Стоимость',
    duration: 'Длительность',
    left: 'Осталось',
    analytics: 'Аналитика',
    purchasedAt: 'Приобретён',
    expiresAt: 'Истекает',
    allPlansExpiredActivateNew:
      'У вас не осталось активных тарифных планов. Для продолжения работы с экспертами активируйте новый ',
    toStartActivatePlan: 'Чтобы начать работать с экспертами, активируйте ',
    suspiciousActivityActivateNew:
      'Тариф заблокирован из-за подозрительной активности. Для продолжения работы с экспертами активируйте новый ',
    suspiciousActivity: 'Тариф заблокирован из-за подозрительной активности.',
    tariffPlan: 'тарифный план',
    allPlansExpired: 'У вас не осталось активных тарифных планов.',
    toContinueContactUs: 'Для продолжения работы с экспертами свяжитесь с нами ',
    toStartContactUs: 'Чтобы начать работать с экспертами свяжитесь с нами ',
    viaPhone: 'по номеру ',
    orEmail: ' или электронной почте ',
    andGetTrialForFree: 'и получите пробный доступ к системе бесплатно',
    purchasedTariffPlans: 'Приобретённые пакеты',
    expiredPlans: 'Истёкшие пакеты',
    currentPlan: 'Активный пакет',

    pricingDescr: 'Вы можете взаимодействовать с экспертом двумя способами:',
    voiceChat: 'Голосовой чат',
    voiceChatDescr: 'получайте ответы эксперта в режиме реального времени через голосовое общение.',
    textChat: 'Текстовый чат',
    textChatDescr: 'задавайте вопросы и получайте точные ответы в текстовом формате.',
    pricingDescr2:
      'В зависимости от способа общения списываются голосовые или текстовые ответы, при этом количество ответов другого типа пропорционально уменьшается.\
      \nКак только вы исчерпаете лимит, вы можете приобрести дополнительный пакет или выбрать другой тариф.',
    setTariff: 'Назначить',
    current: 'Действующий',
    activePlans: 'Тарифы',
  },

  units: {
    [Unit.SECONDS]: 'сек',
  },

  dateSelector: {
    resetCalendar: 'Сбросить диапазон',
    allPeriod: 'Всё время',
    yesterday: 'Вчера',
    today: 'Сегодня',
    calendar: 'Календарь',
    from: 'От',
    to: 'До',
  },

  users: {
    users: 'Пользователи',
    registrationDate: 'Дата регистрации',
    lastLogin: 'Был в системе',
    currentPurchaseExpiresAt: 'Действующий тариф истекает',

    searchPlaceholder: 'Поиск по имени или e-mail',
    searchRefPlaceholder: 'Поиск по рефереру',
    allUsers: 'Все пользователи',
    active: 'Активные',
    disabledUsers: 'Заблокированные',
    disabledUser: 'Заблокирован',
    ref: 'Реферер',
  },

  queryDetailsInfo: {
    title: 'Детали формирования ответа',
    close: 'Закрыть',
    userQuery: 'Запрос:',
    dataSources: 'Источники данных',
    source: 'Источник:',
    file: 'Файл:',
    score: 'Оценка:',
    distance: 'Расстояние',
    systemInstructions: 'Системные инструкции',
    metadata: 'Метаданные',
    finalJsonPrompt: 'Итоговый JSON-запрос (raw_prompt)',
    sourceType: {
      elk: 'ELK',
      vector: 'VECTOR',
      db: 'ИСТОРИЯ',
    },
  },

  systemSettingsFormLabes: {
    welcome_tariff_id: 'Приветственный тариф',
    stt_vad_threshold: 'Порог чувствительности VAD',
    stt_vad_speech_pad_ms: 'Запас аудио по краям',
    system_prompt_constraints: 'Системные ограничения',
    system_prompt_rag: 'Промпт для документов',
    system_prompt_rag_priority: 'Промпт для документов с приоритетом базы знаний',
    system_prompt_images: 'Промпт для изображений',
    system_prompt_tg_operator_text_chat: 'Промпт для работы с Telegram-ботами в текстовом режиме',
    system_prompt_tg_operator_voice_chat: 'Промпт для работы с Telegram-ботами в голосовом режиме',
    system_prompt_tg_operator_disabled:
      'Промпт для работы с Telegram-ботами, если Telegram-боты не заданы',
    system_prompt_dialog_analysis: 'Промпт для анализа диалога',
    system_prompt_dialog_summary_for_operator:
      'Промпт для саммаризации диалога для Telegram-оператора',
    system_prompt_emoji: 'Промпт разрешения смайликов',
    system_prompt_emoji_forbidden: 'Промпт запрета смайликов',
    system_prompt_offtop_forbidden: 'Промпт запрета отвлечённых тем',
    system_prompt_profanity_forbidden: 'Промпт запрета мата',
    system_prompt_profanity_allowed: 'Промпт разрешения мата',
    system_prompt_dialog_finished: 'Промпт для определения завершённости диалога',
    system_prompt_need_operator: 'Промпт для определения необходимости подключить оператора',
    system_prompt_prioritize_operator: 'Промпт приоритизации ответов оператора',
    image_vision_model: 'Модель для анализа изображений',
    system_prompt_generate_starter_suggestions: 'Промпт для генерации стартовых вопросов',
    system_prompt_generate_followup_suggestions:
      'Промпт для генерации вариантов продолжения диалога',
    system_prompt_image_vision: 'Промпт для описания изображений (Vision)',
    system_prompt_channel_rules: 'Базовые правила каналов общения',
    system_prompt_behavior_settings: 'Базовые настройки поведения',
    system_prompt_agent_role_preamble: 'Преамбула роли',
    system_prompt_default_role: 'Роль по умолчанию',
    system_prompt_knowledge_base_context: 'Базовый контекст базы знаний',
    system_prompt_reminder: 'System reminder',
    system_prompt_tools_constraints: 'Ограничения инструментов',
    system_prompt_tools_behavior: 'Поведение при работе с инструментами',
    system_prompt_tools_knowledge_base: 'Навигация по базе знаний',
    execution_profile_pipeline: 'Профиль PIPELINE',
    execution_profile_hybrid: 'Профиль HYBRID',
    execution_profile_agentic: 'Профиль AGENTIC',
  } satisfies Required<Record<keyof SystemSettings, string>>,

  settings: {
    settings: 'Настройки системы',
    recognitionSettings: 'Настройки распознавания',
    recognitionSettingsDescr:
      'Настройте параметры для оптимальной работы VAD и распознавания речи.',
    defaultTariffDescr:
      'Можно выбрать тарифный план, который будет автоматически назначаться новым пользователям при регистрации',
    vadThresholdDescr:
      'Чем выше порог, тем меньше пропускается шумов, что повышает точность распознавания, но возрастает риск пропустить тихие или нечеткие фразы, особенно при плохом качестве микрофона.',
    vadSpeechPadDescr:
      'Cколько дополнительных миллисекунд аудио будет добавлено к началу и концу каждого обнаруженного речевого сегмента.',

    executionProfilesTitle: 'Профили выполнения',
    executionProfilesDescr:
      'Параметры режимов PIPELINE / HYBRID / AGENTIC. Поле verification_strategy вычисляется автоматически по каналу и не хранится здесь.',
    additionalPromptsTitle: 'Дополнительные промпты для сообщений',
    additionalPromptsDescr:
      'Опишите здесь дополнительные инструкции для разных сценариев, для формирования более точных и релевантных ответов.',
    systemPromptVoiceChatDescr:
      'Этот промпт добавляется в запрос к LLM при отправке голосового сообщения. Уклон на корректный синтез.',
    systemPromptTextChatDescr:
      'Этот промпт добавляется в запрос к LLM при отправке текстового сообщения. Уклон на отображение отформатированных данных в чате.',
    systemPromptImagesDescr:
      'Этот промпт добавляется в запрос к LLM, если у эксперта включена опция "Расширенная обработка документов", которая включает в себя обработку изображений.',
    systemPromptForDocumentsDescr:
      'Этот промпт добавляется в запрос к LLM, если к запросу добавлен хотя бы один RAG-чанк.',
    systemPromptRagPriorityDescr:
      'Этот промпт добавляется вместо стандартного RAG-промпта, если у эксперта включено чтение ссылок и выбрана модель Gemini, либо если разрешены отвлечённые темы.',
    systemPromptTgOperatorTextChatDescr:
      'Этот промпт добавляется в запрос к LLM при отправке текстового сообщения, если к эксперту привязаны Telegram-боты.',
    systemPromptTgOperatorVoiceChatDescr:
      'Этот промпт добавляется в запрос к LLM при отправке голосового сообщения, если к эксперту привязаны Telegram-боты.',
    systemPromptTgOperatorDisabledDescr:
      'Этот промпт добавляется в запрос к LLM, если пользователь просит позвать оператора, но к эксперту не привязаны Telegram-боты.',

    servicePromptsTitle: 'Промпты для сервисных запросов',
    servicePromptsDescr: 'Задайте промпты для внутренних запросов системы.',
    systemPromptDialogAnalysisDescr:
      'Промпт для вычленения данных из диалога. \nПеременная CURRENT_DATETIME подставляется в коде для корректного определения относительного времени.\nПеременная ADDITIONAL_FIELDS — это дополнительные поля, вычисляемые при интеграции с Google-таблицами.\nМеняйте эти переменные, только если вы точно знаете что делаете.',
    systemPromptDialogSummaryForOperatorDescr:
      'При переключении диалога на Telegram-оператора оператору придёт сообщение с кратким содержанием предыдущего диалога.',
    systemPromptConstraintsDescr:
      'Иерархия инструкций, защита от инъекций, конфиденциальность системных данных и запрет на домысливание. Добавляется первым в системный промпт.',
    systemPromptEmojiDescr:
      'Применяется, если у эксперта включён переключатель "Смайлики разрешены". Разрешает смайлики, но только если уместно или явно попросили.',
    systemPromptEmojiForbiddenDescr:
      'Применяется, если у эксперта выключен переключатель "Смайлики разрешены". Абсолютный запрет на использование смайликов и эмодзи.',
    systemPromptOfftopForbiddenDescr:
      'Применяется, если у эксперта выключен переключатель "Отвлечённые темы разрешены" и задан системный промпт роли. Ограничивает эксперта рамками его специализации.',
    systemPromptProfanityForbiddenDescr:
      'Применяется, если у эксперта выключен переключатель "Мат разрешён". Абсолютный запрет на ненормативную лексику.',
    systemPromptProfanityAllowedDescr:
      'Применяется, если у эксперта включён переключатель "Мат разрешён". Разрешает использование мата, но без его навязывания.',
    systemPromptDialogFinishedDescr:
      'Если возвращается True, то запускается анализ диалога, результаты которого используются для создания события в календаре и других интеграций. А в голосовом режиме эксперт самостоятельно останавливает запись.',
    systemPromptNeedOperatorDescr:
      'Если возвращается True, то запускается процесс подключения оператора',
    systemPropmptPrioritizeOperatorDescr:
      'Указыват ИИ-эксперту считать ответы человека (оператора) эталонными.',
    systemPromptGenerateStarterSuggestionsDescr:
      'Промпт для генерации 3 стартовых вопросов, которые пользователь видит при первом открытии чата.',
    systemPromptGenerateFollowupSuggestionsDescr:
      'Промпт для генерации 2-3 вариантов продолжения диалога, которые предлагаются пользователю после каждого ответа эксперта.',
    systemPromptImageVisionDescr:
      'Промпт для описания изображений при индексации документов (RAG). Используется vision-моделью для генерации текстового описания изображения.',
    systemPromptAgentRolePreambleDescr:
      'Добавляется первым в секцию <agent_role_and_goal> безусловно. Поясняет модели, что далее идёт роль эксперта.',
    systemPromptDefaultRoleDescr:
      'Добавляется вместо роли эксперта, если системный промпт не задан в настройках эксперта.',
    systemPromptChannelRulesDescr:
      'Добавляется первым в секцию <channel_rules> системного промпта безусловно, независимо от настроек эксперта.',
    systemPromptBehaviorSettingsDescr:
      'Добавляется первым в секцию <behavior_settings> системного промпта безусловно, независимо от настроек эксперта.',
    systemPromptKnowledgeBaseContextDescr:
      'Добавляется первым в секцию <knowledge_base_context> системного промпта безусловно, независимо от статуса обучения и настроек эксперта.',
    systemPromptReminderDescr:
      'Короткое напоминание модели в конце системного промпта — придерживаться роли и правил.',

    toolsPromptsTitle: 'Промпты для режима инструментов',
    toolsPromptsDescr:
      'Дополнительные инструкции для экспертов с включёнными инструментами (use_tools). Текст семантически разделён на три части — каждая добавляется в соответствующий блок системного промпта: system_constraints, behavior_settings и knowledge_base_context.',
    systemPromptToolsConstraintsDescr:
      'Жёсткие запреты и ограничения при работе с инструментами. Добавляется в блок <system_constraints> системного промпта.',
    systemPromptToolsBehaviorDescr:
      'Поведенческие паттерны и правила работы с инструментами. Добавляется в блок <behavior_settings> системного промпта.',
    systemPromptToolsKnowledgeBaseDescr:
      'Инструкции по навигации в базе знаний: использование document_id, стратегия обхода, работа с изображениями. Добавляется в блок <knowledge_base_context> системного промпта.',
  },

  docs: {
    docs: 'Документация',
  },

  support: { support: 'Техподдержка' },

  experimentalFeature: 'Эксперементальная функция',
};
